import React from "react";
import { useTranslation } from "react-i18next";
import { Dropdown } from "antd";
import { Button } from "src/components/ui/Button";

const languages = [
  { code: "en", name: "ENG", flag: "🇬🇧" },
  { code: "tr", name: "TR", flag: "🇹🇷" },
  { code: "ja", name: "JA", flag: "🇯🇵" },
];

const TriggerCaret = ({ open }) => (
  <svg
    className={`h-3.5 w-3.5 shrink-0 text-Gray500 transition-transform duration-200 ${
      open ? "rotate-180" : ""
    }`}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    viewBox="0 0 24 24"
    aria-hidden
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

const SelectedCheck = () => (
  <svg
    className="h-3.5 w-3.5 shrink-0 text-[color:var(--flow-border-accent)]"
    fill="currentColor"
    viewBox="0 0 20 20"
    aria-hidden
  >
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const LanguageSwitcher = () => {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const currentLanguage =
    languages.find((lang) => lang.code === i18n.language) || languages[0];

  const handleSelect = ({ key }) => {
    i18n.changeLanguage(key);
    setOpen(false);
  };

  const items = languages.map((language) => {
    const selected = currentLanguage.code === language.code;
    return {
      key: language.code,
      label: (
        <div
          className={`flex items-center gap-2.5 text-xs font-medium ${
            selected ? "text-Gray950" : "text-Gray700"
          }`}
        >
          <span className="text-[1.05rem] leading-none" aria-hidden>
            {language.flag}
          </span>
          <span className="flex-1">{language.name}</span>
          {selected && <SelectedCheck />}
        </div>
      ),
    };
  });

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={["click"]}
      placement="bottomRight"
      menu={{
        items,
        selectable: true,
        selectedKeys: [currentLanguage.code],
        onClick: handleSelect,
        className: "min-w-[10.5rem]",
      }}
    >
      <Button
        variant=""
        htmlType="button"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={t("common.language", { defaultValue: "Language" })}
        className="flex h-8 items-center gap-1.5 rounded-full border border-black/[0.07] bg-white/75 py-1 pl-2 pr-1.5 text-Gray700 shadow-[0_1px_3px_rgb(15_23_42/0.06)] backdrop-blur-md transition-[background-color,box-shadow,transform] duration-200 hover:border-black/[0.1] hover:bg-white/95 hover:shadow-[0_2px_8px_rgb(15_23_42/0.08)] active:scale-[0.98] supports-[backdrop-filter]:bg-white/65"
      >
        <span className="text-[1.05rem] leading-none" aria-hidden>
          {currentLanguage.flag}
        </span>
        <span className="max-w-[2.25rem] truncate text-xs font-semibold tracking-wide text-Gray900">
          {currentLanguage.name}
        </span>
        <TriggerCaret open={open} />
      </Button>
    </Dropdown>
  );
};

export default LanguageSwitcher;
